import { Box, Card, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { animate, useMotionValue, useMotionValueEvent } from 'framer-motion';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { CandidateProfile } from 'src/services/candidates/candidates.types';

type Props = {
  profile: CandidateProfile
};

const CompletionIconContainer = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
});

const BackgroundCircle = styled(CircularProgress)({
  color: '#e0e0e0', // grey for the unfilled portion
  position: 'absolute',
});

const ProgressCircle = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const ProfileCompletionCard = ({ profile }: Props) => {
  const completionPercentage = profile.completion_percentage || 0;

  // initialize the MotionValue
  const motionPercentage = useMotionValue(0);

  // state to hold the display value, triggering re-renders
  const [displayPercentage, setDisplayPercentage] = useState(motionPercentage.get());

  // animate the MotionValue when completionPercentage changes
  useEffect(() => {
    animate(motionPercentage, completionPercentage, {
      duration: 3,
      ease: 'easeOut',
    });
  }, [completionPercentage, motionPercentage]);

  // update state when motionPercentage changes
  useMotionValueEvent(motionPercentage, 'change', (latest) => {
    setDisplayPercentage(Math.round(latest));
  });

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 2,
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
      }}
    >
      <Box>
        <Box>
          <Typography variant="h6" component="div" color="text.primary">
            {t('profile.completion.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('profile.completion.percentage', { percentage: displayPercentage })}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        {displayPercentage < 100 ? (
          <CompletionIconContainer>
            <BackgroundCircle variant="determinate" value={100} size={30} thickness={4} />
            <ProgressCircle variant="determinate" value={displayPercentage} size={30} thickness={4} />
          </CompletionIconContainer>
        ) : (
          <Iconify icon="solar:check-circle-broken" color="#4caf50" width={30} />
        )}
      </Box>
    </Card>
  );
};

export default ProfileCompletionCard;